import styled, { css } from 'styled-components';
import { pxToRem } from 'modules/spacing';
import { theme } from 'modules/tetrisTheme';
const ADORNMENT_WIDTH = 40;
export const StyledTextArea = styled.textarea `
  border: none;
  background-color: transparent;
  flex: 1 1 100%;
  font-size: ${theme.fontSize.medium};
  line-height: ${theme.lineHeight.default};
  color: ${theme.semantic.foreground.primary};
  resize: ${(props) => props.$resize};

  ${(props) => props.$variant === 'outline' &&
    `
    box-sizing: content-box;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    min-height: ${`calc(${pxToRem(props.$minHeight * 4)} - ${theme.spacing.md} * 2)`};
    max-height: ${props.$maxHeight ? `calc(${pxToRem(props.$maxHeight * 4)} - ${theme.spacing.md} * 2)` : 'none'};
  `};

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    color: ${theme.swatches.warm[500]};
  }

  &:not(:first-child) {
    padding-left: ${pxToRem(ADORNMENT_WIDTH)};
  }

  &:not(:last-child) {
    padding-right: ${pxToRem(ADORNMENT_WIDTH)};
  }
`;
export const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  position: relative;
  width: ${(props) => props.$stretch && '100%;'};
`;
export const Adornment = styled.div `
  position: absolute;
  top: 0;
  width: ${pxToRem(ADORNMENT_WIDTH)};
  height: ${pxToRem(ADORNMENT_WIDTH)};
  display: flex;
  align-items: center;
  justify-content: center;

  &:first-child {
    left: 0;
  }

  &:last-child {
    right: ${pxToRem(16)}; /* This is the width of the scrollbar, this prevents overlapping */
  }
`;
const descriptionInvalidStyles = css `
  color: ${theme.semantic.text.danger};
`;
export const Description = styled.div `
  font: ${theme.fontRole.caption};
  color: ${theme.semantic.foreground.primary};
  display: inline-block;
  margin-top: ${theme.spacing.sm};

  &:has(+ * textarea:disabled) {
    color: ${theme.swatches.warm[500]};
  }

  &:has(+ * textarea:focus-visible) {
    color: ${(props) => !props.$invalid && theme.semantic.foreground.primary};
  }

  ${(props) => props.$invalid && descriptionInvalidStyles};
`;
const outlineInputDisabledStyles = css `
  border: ${pxToRem(1)} dashed ${theme.semantic.border.strong};
  box-shadow: none;
  margin: ${pxToRem(1)}; /* prevents the input border from jumping */
`;
const outlineInputDefaultStyles = css `
  background-color: ${theme.semantic.surface.primary};
  box-shadow: 0 0 0 ${pxToRem(1)} ${theme.semantic.border.default}; /* firefox fallback until :has selector is supported */

  &:has(textarea:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${theme.semantic.border.default};
  }

  &:hover:not(:has(textarea:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.border.default};
  }

  &:has(textarea:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.warm[700]};

    ${Adornment} svg {
      color: ${theme.semantic.foreground.primary};
    }
  }

  &:has(textarea:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.warm[700]};
  }

  &:has(textarea:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputHighlightStyles = css `
  background-color: ${theme.swatches.blue[50]};
  box-shadow: 0 0 0 ${pxToRem(1)} ${theme.swatches.blue[500]};

  &:hover:not(:has(textarea:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.blue[500]};
  }

  &:has(textarea:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.blue[500]};

    ${Adornment} svg {
      color: ${theme.swatches.blue[500]};
    }
  }

  &:has(textarea:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.swatches.blue[500]};
  }

  &:has(textarea:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputInvalidStyles = css `
  background-color: ${theme.semantic.surface.primary};
  box-shadow: 0 0 0 ${pxToRem(1)} ${theme.semantic.text.danger};

  &:hover:not(:has(textarea:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.text.danger};
  }

  &:has(textarea:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.text.danger};

    ${Adornment} svg {
      color: ${theme.semantic.text.danger};
    }
  }

  &:has(textarea:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${theme.semantic.text.danger};
  }

  &:has(textarea:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const transparentInputStyles = css `
  border: transparent;
  outline: transparent;
`;
export const TextAreaContainer = styled.div `
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  border-radius: ${theme.borderRadius.md};
  ${(props) => props.$appearance === undefined && !props.$invalid && outlineInputDefaultStyles};
  ${(props) => props.$appearance === 'highlight' && outlineInputHighlightStyles};
  ${(props) => props.$invalid && outlineInputInvalidStyles};
  ${(props) => props.$appearance === 'transparent' && transparentInputStyles};
  margin: ${pxToRem(2)}; /* prevent box-shadow border from being hidden/covered in case of container not having any padding */

  transition: box-shadow 0.15s;
`;
